import type { Pagination } from './index';

/** Mirrors backend services/email — the Email pages and each record's emails. */

export type MailProvider = 'gmail' | 'outlook' | 'zoho' | 'other';
export type EmailStatus = 'sending' | 'sent' | 'failed';
export type EmailSenderKind = 'edubook' | 'own';

export interface MailProviderInfo {
  key: MailProvider;
  label: string;
  host: string;
  port: number;
  secure: boolean;
  dailyLimit: number;
}

export interface EmailTypeSetting {
  key: string;
  group: string;
  /** Permission module of the records it's about; writing them allows Resend. */
  module: string;
  label: string;
  description: string;
  enabled: boolean;
  /** The school's own wording: only the fields it changed. */
  copy: Partial<EmailCopy>;
  /** EduBook's wording. */
  defaults: EmailCopy;
  /** What may go in braces, e.g. {Child name}. */
  placeholders: EmailPlaceholder[];
}

/** An email's words (mirror of backend services/email/emailCopy). Body paragraphs are split by a blank line. */
export interface EmailCopy {
  subject: string;
  heading: string;
  body: string;
  /** Only on emails that have a button. */
  button?: string;
}

export interface EmailPlaceholder {
  token: string;
  hint: string;
}

export interface OwnMailbox {
  provider: MailProvider;
  username: string;
  host: string;
  port: number;
  secure: boolean;
  verifiedAt: string;
  /** Set when the mailbox stopped accepting our sign-in; mail goes from EduBook meanwhile. */
  failedAt: string | null;
  lastError: string | null;
}

export interface EmailSettings {
  fromName: string;
  replyTo: string;
  /** What empty fromName / replyTo fall back to: the school's name and email. */
  defaults: { fromName: string; replyTo: string };
  platform: { ready: boolean; address: string };
  own: OwnMailbox | null;
  providers: MailProviderInfo[];
  types: EmailTypeSetting[];
  usage: { sentToday: number; limit: number };
}

export interface EmailSettingsPatch {
  fromName?: string;
  replyTo?: string;
  types?: Array<{ type: string; enabled?: boolean; copy?: EmailCopy }>;
}

export interface MailboxInput {
  provider: MailProvider;
  username: string;
  password: string;
  host?: string;
  port?: number;
}

/** The admission emails by name (mirror of backend ADMISSION_EMAIL). */
export const ADMISSION_EMAIL = {
  documentsResend: 'admission_documents_resend',
  offered: 'admission_offered',
  notOffered: 'admission_not_offered',
} as const;

/** Whether an email goes out, and each record's address (none when it has no email on file). */
export interface UpcomingEmail {
  type: string;
  on: boolean;
  recipients: Record<string, string[]>;
}

export interface EmailPreview {
  subject: string;
  html: string;
  /** Sender name and the address it goes from. */
  from: string;
  fromAddress: string;
  /** The made-up family's address. */
  to: string;
  replyTo: string;
}

export interface EmailLogEntry {
  _id: string;
  type: string;
  typeLabel: string;
  entityType: string;
  entityId: string;
  entityLabel: string;
  to: string[];
  subject: string;
  status: EmailStatus;
  error: string | null;
  sender: EmailSenderKind | null;
  sentAt: string | null;
  sentBy: { _id: string; name: string } | null;
  createdAt: string;
}

export interface EmailLogResponse {
  items: EmailLogEntry[];
  pagination: Pagination;
  counts: Record<EmailStatus, number>;
}

export interface RecordEmails {
  items: EmailLogEntry[];
  /** Where this record's emails would go now. */
  recipients: string[];
}
